import { AnalyticsBrowser } from '@segment/analytics-next'
import type { Analytics } from '@segment/analytics-next'
import { Settings } from '../../app/Settings'
import { QueryKeys } from '../api/session/queryKeys'
import { sessionReplayManager } from './amplitude'
import type { AnalyticsProperties, AnalyticsUser, ServerTarget } from './types'
import { getUserTraits, isAnalyticsEnabled, isDebugActive, withPlatformSuffix } from './utils'

/**
 * The web app's Segment client — the only event destination the browser sends to today.
 * Amplitude is reached separately, for session replay only (`amplitudeSessionReplay`).
 *
 * It owns its own methods and holds the SDK as a member, never reassigning the SDK's own,
 * so a call site cannot typecheck against a signature the runtime does not honour.
 *
 * Loading is deliberately lazy: the Segment bundle is fetched on the first call that the
 * guard allows, so an excluded user never downloads it.
 *
 * The singleton is exported as `analytics` on purpose. Feature code names the capability,
 * not the vendor; the day a second event destination appears, `analytics` becomes an
 * aggregator over this client — the shape mobile already has — without touching a single
 * call site.
 */
class SegmentClient {
  private client: Analytics | null = null

  private loading: Promise<Analytics> | null = null

  // Resolved once, when the SDK loads, so every event in a session reports the same target
  // even if the debug panel is toggled mid-session.
  private server: ServerTarget = 'production'

  // Who the last successful login identify was for. See `identify`.
  private identifiedUserId: string | null = null

  private load(): Promise<Analytics> {
    if (this.client) return Promise.resolve(this.client)
    if (this.loading) return this.loading

    const debugActive = isDebugActive()
    const writeKey = debugActive ? Settings.ApiCredentials.Segment.DEV : Settings.ApiCredentials.Segment.PROD

    this.loading = AnalyticsBrowser.load({ writeKey })
      .then(([client]) => {
        this.server = debugActive ? 'development' : 'production'
        this.client = client

        // The SDK is deliberately not published on `window`. Nothing reads it — not this
        // repo, and not the SDK itself: its pre-init buffer returns early for npm
        // installs, and the CDN and write-key lookups only read properties the classic
        // snippet writes. Inspect traffic in the network tab instead.

        return client
      })
      .catch(error => {
        // A blocked or flaky first load must not disable analytics for the rest of the
        // session — tracking-prevention lists cover cdn.segment.com, so this is a routine
        // failure, not an exceptional one. Drop the promise so the next call can retry.
        this.loading = null
        throw error
      })

    return this.loading
  }

  /** The SDK when analytics is allowed to send right now, otherwise null. The only gate. */
  private ready = async (): Promise<Analytics | null> => {
    if (!isAnalyticsEnabled()) return null
    return this.load()
  }

  page = async (properties?: AnalyticsProperties) => {
    const client = await this.ready()
    return client?.page(properties)
  }

  track = async (event: string, properties: AnalyticsProperties = {}) => {
    const client = await this.ready()
    if (!client) return

    const name = withPlatformSuffix(event)
    const payload = {
      ...properties,
      server: this.server,
      ...sessionReplayManager.getCurrentSessionReplayProperties()
    }

    logger.log(name, payload)

    return client.track(name, payload)
  }

  identify = async ({ user, isLoggedIn }: { user: AnalyticsUser; isLoggedIn: boolean }) => {
    // A login identify is idempotent per user. Its caller is an effect that re-runs
    // whenever the login flag flips — which a profile refetch can do without the user
    // changing — and re-sending an identical profile is noise, not data. A logout
    // identify (`isLoggedIn: false`) is never deduped: it is the only thing that reports
    // the session ending, and it clears the marker for the next user.
    //
    // The claim has to happen synchronously, before the first `await`. Two effects firing
    // in the same tick both wait on the same SDK load, resolve together, and would both
    // see an unclaimed id — which is exactly what produced pairs of identify calls a
    // second apart in production.
    if (isLoggedIn && this.identifiedUserId === user?.id) return

    this.identifiedUserId = isLoggedIn ? user?.id : null

    const client = await this.ready()

    if (!client) {
      // Nothing was sent, so release the claim rather than suppress a later attempt.
      this.identifiedUserId = null
      return
    }

    sessionReplayManager.identify(user?.id)

    const traits = getUserTraits(user, isLoggedIn, this.server, client.user().anonymousId())

    return client.identify(user?.id, traits)
  }

  /**
   * Merges partial traits onto the already-identified user, resolving them from the session
   * query rather than taking an id. Use it when a single trait changes after login (badges,
   * for example); the whole-profile mapping stays owned by `identify`.
   */
  updateTraits = async (traits: AnalyticsProperties) => {
    // Resolved before `ready()` so a caller with no identified user never triggers the
    // SDK download for a call that cannot land anywhere.
    const id = QueryKeys.me.getData()?.id
    if (!id) return

    const client = await this.ready()
    if (!client) return

    return client.identify(String(id), traits)
  }

  reset = async () => {
    this.identifiedUserId = null

    const client = await this.ready()
    return client?.reset()
  }
}

export const analytics = new SegmentClient()
